import {
  Flex,
  VStack,
  Image,
  Box,
  IconButton,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  HStack,
  Text
} from '@chakra-ui/react'
import { useAnimation, Variants } from 'framer-motion'
import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { ColorProps } from 'styled-system'
import { useAppContext } from '../../../context/AppProvider'
import ProfileButton from '../../UI/Buttons/ProfileButton'
import { NavItems } from '../../../navigation'
import { images } from '../../../theme'
import SideBarItem, { MenuUserProfile } from './SideBarItem'
import { MenuCont, MenuCont2, Overlay, RenderWrapper } from './styles'
import { useHistory } from 'react-router-dom'
import SideBarButton from '../SideBar/SideBarButton'
import CustomTooltip from 'components/UX/CustomTooltip'
import QuickViewer from '../../UX/QuickViewer'
import { VscChevronLeft, VscClose } from 'react-icons/vsc'
import { useBrowserStorage } from 'hooks'
import { ACTIVE_DRAWER_ITEMS } from '../../../constants'

const DRAWER_SIZE = 350
const SECONDARY_DRAWER_SIZE = 350
const SIDEBAR_OPEN_SIZE = 232
const SIDEBAR_COLLAPSED_SIZE = 88

type SideBarProps = ColorProps & {
  accentColor: string
  borderColor?: string
  closeOnNavigate?: boolean
  color: string
  hoverColor: string
  navItems: NavItems
  renderUserInfo?: (history: any) => React.ReactNode
  tooltipBg?: string
  tooltipColor?: string
  children?: React.ReactNode
}

const SideBar: React.FC<SideBarProps> = ({
  accentColor,
  bg,
  borderColor,
  children,
  color,
  hoverColor,
  navItems,
  tooltipBg,
  tooltipColor
}) => {
  const {
    drawerOpen,
    toggleDrawer,
    drawerOpen2,
    toggleDrawer2,
    rightSidebarWidth,
    setRightSidebarWidth
  } = useAppContext()

  const [isDragging, setIsDragging] = React.useState(false)
  const [isResizeHovered, setIsResizeHovered] = React.useState(false)

  const startResize = React.useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  React.useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX
      if (newWidth >= 260 && newWidth <= 700) setRightSidebarWidth(newWidth)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return
      const newWidth = window.innerWidth - e.touches[0].clientX
      if (newWidth >= 260 && newWidth <= 700) setRightSidebarWidth(newWidth)
    }

    const handleEnd = () => setIsDragging(false)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleEnd)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleEnd)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleEnd)
    }
  }, [isDragging, setRightSidebarWidth])

  const controls = useAnimation()
  const history = useHistory()

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 48em)' })
  const [activeDrawerItems, setActiveDrawerItems] = useBrowserStorage<number[]>(
    ACTIVE_DRAWER_ITEMS,
    'local',
    [0]
  )
  const [extendedItems, setExtendedItems] = React.useState(
    activeDrawerItems && activeDrawerItems.length > 0 ? activeDrawerItems : [0]
  )

  React.useEffect(() => {
    if (drawerOpen) {
      controls.start('open')
    } else {
      controls.start('closed')
    }
  }, [isTabletOrMobile, drawerOpen, controls])

  const variants: Variants = {
    open: {
      x: 0,
      width: SIDEBAR_OPEN_SIZE,
      transition: { staggerChildren: 0.2, delayChildren: 0.1, stiffness: 80 }
    },
    closed: {
      x: isTabletOrMobile ? -DRAWER_SIZE : 0,
      width: isTabletOrMobile ? DRAWER_SIZE : SIDEBAR_COLLAPSED_SIZE,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.2,
        delay: isTabletOrMobile ? 0.25 : 0
      }
    }
  }

  return (
    <>
      <MenuCont
        bg={bg as any}
        flexDir="column"
        animate={controls}
        variants={variants}
        alignItems="flex-start"
        justifyContent="flex-start"
        initial={{ width: drawerOpen ? SIDEBAR_OPEN_SIZE : SIDEBAR_COLLAPSED_SIZE }}
      >
        <VStack spacing={4} pb={2}>
          <Flex
            width="80%"
            height="72px"
            alignItems="center"
            borderBottomWidth={1}
            justifyContent="flex-start"
            borderColor="gray.200"
            my={3}
            bg="gray.900"
            transition="all 0.2s ease"
            rounded="0"
            cursor="pointer"
          >
            <Flex m={0} p={0} flex={1} justifyContent="center">
              <Image
                style={{
                  filter: 'grayscale(1) brightness(0) invert(1)'
                }}
                onClick={() => history.push('/auth/tasks')}
                width={drawerOpen ? '60%' : '45%'}
                src={drawerOpen ? images.logo : images.logoNoText}
                alt="Baotree Icon"
                transition="all 0.2s ease"
              />
            </Flex>
          </Flex>
          <Flex justify="center" w="80%" align="center">
            {drawerOpen && <MenuUserProfile />}
            <CustomTooltip label={drawerOpen ? 'Close Menu' : 'Open Menu'}>
              <Box
                border="1px solid"
                borderColor={drawerOpen ? 'gray.200' : 'transparent'}
                p={2.5}
                rounded="0"
                position={drawerOpen ? 'absolute' : 'relative'}
                right={0}
                bg="gray.100"
                className="humberger-menu-button"
                transform={drawerOpen ? 'translateX(50%)' : 'translateX(0)'}
                transition="all 0.2s ease"
                _hover={{ bg: 'gray.200' }}
              >
                <SideBarButton color="black" open={drawerOpen} onClick={toggleDrawer} />
              </Box>
            </CustomTooltip>
          </Flex>
        </VStack>
        <VStack
          direction="column"
          overflowY={drawerOpen ? 'auto' : undefined}
          overflowX={drawerOpen ? 'hidden' : undefined}
          width="100%"
          flex={1}
          pt={4}
          pb={16}
          maxH={drawerOpen ? 'calc(100vh - 172px)' : '100%'}
        >
          <Accordion
            width="100%"
            p={0}
            borderColor="transparent"
            allowMultiple={drawerOpen ? true : false}
            allowToggle
            onChange={(expandedIndex) => {
              const indices = Array.isArray(expandedIndex)
                ? expandedIndex
                : expandedIndex >= 0
                  ? [expandedIndex]
                  : []
              setExtendedItems(indices as number[])
              setActiveDrawerItems(indices as number[])
            }}
            defaultIndex={
              activeDrawerItems && activeDrawerItems.length > 0 ? activeDrawerItems : [0]
            }
          >
            {Object.keys(navItems).map((nav, index) => (
              <AccordionItem border="none" mx={drawerOpen ? 3 : 4} mb={1} rounded="0" key={index}>
                {({ isExpanded }) => {
                  return (
                    <Box
                      position="relative"
                      rounded="0"
                      bg={isExpanded && !drawerOpen ? 'green.50' : 'transparent'}
                      transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                      overflow="hidden"
                    >
                      {drawerOpen && isExpanded && (
                        <Box
                          position="absolute"
                          left={0}
                          top="10px"
                          bottom="10px"
                          w="2px"
                          bg="brand.500"
                        />
                      )}
                      <CustomTooltip label={nav} placement="right" textTransform="capitalize">
                        <AccordionButton
                          rounded="0"
                          bg={drawerOpen && isExpanded ? 'brand.50' : 'transparent'}
                          py={3}
                          px={drawerOpen ? 4 : 0}
                          minH="44px"
                          _hover={{ bg: drawerOpen && isExpanded ? 'brand.100' : 'gray.100' }}
                          transition="all 0.2s ease"
                          justifyContent={drawerOpen ? 'space-between' : 'center'}
                        >
                          <HStack
                            as="span"
                            flex={drawerOpen ? 1 : undefined}
                            minW={0}
                            spacing={3}
                            textAlign="left"
                            justify={drawerOpen ? 'flex-start' : 'center'}
                          >
                            <Flex
                              as="span"
                              boxSize={5}
                              align="center"
                              justify="center"
                              flexShrink={0}
                              bg={drawerOpen && isExpanded ? 'white' : 'transparent'}
                              color={drawerOpen && isExpanded ? 'brand.500' : color}
                              border="1px solid"
                              borderColor={drawerOpen && isExpanded ? 'brand.100' : 'transparent'}
                            >
                              {navItems[nav].icon}
                            </Flex>
                            {drawerOpen && (
                              <Text
                                color={isExpanded ? 'brand.700' : color}
                                textTransform="uppercase"
                                fontWeight={isExpanded ? '700' : '600'}
                                fontSize="2xs"
                                letterSpacing="0.06em"
                                as="span"
                                noOfLines={1}
                              >
                                {nav}
                              </Text>
                            )}
                          </HStack>
                          {drawerOpen && (
                            <AccordionIcon
                              color={isExpanded ? 'brand.500' : 'gray.400'}
                              flexShrink={0}
                            />
                          )}
                        </AccordionButton>
                      </CustomTooltip>
                      <AccordionPanel pb={2} pr={0} pl={0} borderRadius="0">
                        <VStack spacing={0}>
                          {navItems[nav]?.items?.map((props) => (
                            <SideBarItem
                              color={color}
                              key={props.title}
                              hoverColor={hoverColor}
                              accentColor={accentColor}
                              tooltipColor={tooltipColor}
                              tooltipBg={tooltipBg}
                              {...props}
                            />
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </Box>
                  )
                }}
              </AccordionItem>
            ))}
          </Accordion>
        </VStack>
      </MenuCont>

      <MenuCont2
        bg="gray.50"
        flexDir="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        w={drawerOpen2 ? `${rightSidebarWidth}px` : '100px'}
        pt={20}
        overflowY={drawerOpen2 ? 'auto' : undefined}
        overflowX={drawerOpen2 ? 'hidden' : undefined}
        transition={isDragging ? 'none' : 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)'}
      >
        {/* ── Resize handle — always visible grip rail ── */}
        {drawerOpen2 && (
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            w="10px"
            cursor="col-resize"
            zIndex={1305}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onMouseDown={startResize}
            onTouchStart={startResize}
            onMouseEnter={() => setIsResizeHovered(true)}
            onMouseLeave={() => setIsResizeHovered(false)}
            aria-label="Drag to resize panel"
            role="separator"
          >
            <Box
              w="2px"
              h="48px"
              rounded="full"
              bg={isDragging || isResizeHovered ? 'brand.500' : 'gray.200'}
              transition={isDragging ? 'none' : 'background 0.15s'}
            />
          </Box>
        )}

        {/* ── Fixed header bar ── */}
        <Flex
          align="center"
          justify={drawerOpen2 ? 'space-between' : 'center'}
          bg="white"
          py={4}
          position="fixed"
          width={drawerOpen2 ? `${rightSidebarWidth}px` : '100px'}
          px={drawerOpen2 ? 6 : 0}
          top={0}
          zIndex={1000}
          transition={isDragging ? 'none' : 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'}
        >
          <IconButton
            onClick={() => toggleDrawer2()}
            cursor="pointer"
            rounded="0"
            color={drawerOpen2 ? 'red.300' : 'gray.600'}
            border="1px solid"
            variant="outline"
            borderColor={drawerOpen2 ? 'red.200' : 'gray.200'}
            position={drawerOpen2 ? 'relative' : 'absolute'}
            bg="white"
            left={0}
            transform={drawerOpen2 ? 'translateX(0)' : 'translateX(-50%)'}
            px={2}
            py={4}
            aria-label={drawerOpen2 ? 'Close Menu' : 'Open Menu'}
            _hover={{
              bg: drawerOpen2 ? 'red.300' : 'gray.100',
              borderColor: drawerOpen2 ? 'transparent' : 'gray.200',
              color: drawerOpen2 ? 'white' : 'gray.900'
            }}
          >
            {drawerOpen2 ? <VscClose /> : <VscChevronLeft />}
          </IconButton>
          <ProfileButton />
        </Flex>

        <QuickViewer isOpen={drawerOpen2} />
      </MenuCont2>

      <RenderWrapper
        animate
        className="render-wrapper"
        pl={
          isTabletOrMobile
            ? 0
            : drawerOpen
              ? `${SIDEBAR_OPEN_SIZE}px`
              : `${SIDEBAR_COLLAPSED_SIZE}px`
        }
        pr={isTabletOrMobile ? 0 : drawerOpen2 ? `${rightSidebarWidth}px` : '100px'}
        transition={isDragging ? 'none' : 'padding-right 0.2s cubic-bezier(0.4, 0, 0.2, 1)'}
      >
        {children}
        {isTabletOrMobile && (
          <Overlay
            onClick={toggleDrawer}
            initial={{ opacity: 0 }}
            pointerEvents={drawerOpen ? 'auto' : 'none'}
            animate={drawerOpen ? { opacity: 1 } : { opacity: 0 }}
          />
        )}
      </RenderWrapper>
    </>
  )
}

export default SideBar

SideBar.defaultProps = {
  color: 'gray.500',
  bg: 'white',
  hoverColor: 'gray.100',
  borderColor: 'gray.200',
  accentColor: 'gray.900'
}
